/* Sitzung mit dem optionalen Backend: Verbindung, Anmeldung und Abgleich der Benutzereinstellungen.
 * Hält den Zustand an einer Stelle; Oberflächen (React oder DOM) lesen ihn über subscribe/getSnapshot.
 * Ohne DOM: Speicher, Client-Fabrik und Einstellungs-Anbindung werden übergeben, damit sich alles mit node prüfen lässt. */
import Backend from './backend.ts';
import type { BackendClient, BackendUser, ClientOptions, HealthInfo } from './backend.ts';
import UserSettings from './settings.ts';
import type { Allow, Snapshot } from './settings.ts';

export type ConnState = 'unknown' | 'ok' | 'down';

/** Unveränderlicher Stand für Oberflächen; ändert sich nur, wenn sich etwas geändert hat */
export interface SessionSnapshot {
  version: number;
  state: ConnState;
  info: HealthInfo | null;
  /** Server-URL (leer = gleiche Origin) */
  base: string;
  email: string;
  user: BackendUser | null;
  authed: boolean;
}

/** Wie die Einstellungen lokal gelesen und angewendet werden (Sprache, Design, Tastenkürzel) */
export interface SettingsPort {
  allow(): Allow;
  read(): { lang?: string; theme?: Snapshot['theme']; keys?: Record<string, string[]> };
  apply(part: Snapshot): void;
}

export type SessionNotice = { type: 'session-expired' } | { type: 'settings-failed'; msg: string };

export interface SessionDeps {
  storage: { getItem(k: string): string | null; setItem(k: string, v: string): void };
  settings: SettingsPort;
  notify?: (n: SessionNotice) => void;
  /** Fabrik für den Client (Tests); Standard: Backend.create */
  makeClient?: (o: ClientOptions) => BackendClient;
  fetch?: ClientOptions['fetch'];
}

type Sync = ReturnType<typeof UserSettings.createSync>;

export class BackendSession {
  client: BackendClient | null = null;
  state: ConnState = 'unknown';
  info: HealthInfo | null = null;
  email = '';
  sync: Sync | null = null;
  private deps: SessionDeps;
  private listeners = new Set<() => void>();
  private snap: SessionSnapshot;
  private version = 0;

  constructor(deps: SessionDeps) {
    this.deps = deps;
    this.snap = this.build();
  }

  private build(): SessionSnapshot {
    const c = this.client;
    return { version: this.version, state: this.state, info: this.info, base: c ? c.base : '', email: this.email, user: c ? c.user : null, authed: !!(c && c.token) };
  }
  private emit(): void {
    const next = this.build(), a = this.snap;
    if (next.state === a.state && next.info === a.info && next.base === a.base && next.email === a.email && next.user === a.user && next.authed === a.authed) return;
    this.version++; next.version = this.version;
    this.snap = next;
    this.listeners.forEach((f) => f());
  }

  /** Für useSyncExternalStore: liefert eine Funktion zum Abmelden des Zuhörers */
  subscribe = (fn: () => void): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  getSnapshot = (): SessionSnapshot => this.snap;

  /** Gemerkten Zustand schreiben (URL, Token, Benutzer, E-Mail) */
  save(): void {
    const c = this.client;
    if (c) Backend.saveSettings(this.deps.storage, { base: c.base, token: c.token, email: this.email, user: c.user });
  }

  /** Verbindet (mit der gemerkten oder einer neuen URL), prüft den Server und startet den Einstellungsabgleich, wenn angemeldet. */
  async connect(base?: string): Promise<boolean> {
    const st = Backend.loadSettings(this.deps.storage);
    if (base !== undefined) st.base = Backend.normBase(base);
    const make = this.deps.makeClient || Backend.create;
    this.client = make({
      base: st.base, token: st.token, user: st.user, fetch: this.deps.fetch,
      onAuthLost: () => { this.save(); this.deps.notify?.({ type: 'session-expired' }); this.ready(); this.emit(); }
    });
    this.email = st.email; this.state = 'unknown'; this.info = null;
    this.emit();
    try { this.info = await this.client.health(); this.state = 'ok'; } catch (e) { this.info = null; this.state = 'down'; }
    this.save();
    if (this.sync) this.sync.stop();
    const port = this.deps.settings;
    this.sync = UserSettings.createSync({
      client: this.client, allow: port.allow(), read: () => port.read(), apply: (p) => port.apply(p),
      onStatus: (k, info) => { if (k === 'error') this.deps.notify?.({ type: 'settings-failed', msg: (info as Error | undefined)?.message || '' }); }
    });
    this.ready();
    this.emit();
    return this.state === 'ok';
  }

  /** Nach Verbindung, Anmeldung oder Abmeldung: Einstellungen abgleichen bzw. den Abgleich beenden */
  ready(): void {
    if (!this.sync || !this.client) return;
    if (this.state === 'ok' && this.client.token) void this.sync.pull(); else this.sync.stop();
  }

  /** Anmelden; wirft bei falschen Zugangsdaten (Meldung des Servers) */
  async login(email: string, password: string): Promise<void> {
    if (!this.client) throw new Error('nicht verbunden');
    const e = email.trim();
    await this.client.login(e, password);
    this.email = e;
    this.save(); this.ready(); this.emit();
  }

  logout(): void {
    if (!this.client) return;
    this.client.setToken('');
    this.save(); this.ready(); this.emit();
  }

  /** Lokale Einstellungen haben sich geändert (Sprache, Design, Tastenkürzel) */
  settingsChanged(): void { this.sync?.changed(); }
}
